/**
 * AFS Legal OS — Research Assistant Dashboard
 *
 * Roadmap ref: 6d (Research Assistant — dashboard entry point)
 *
 * REDESIGN — query-only, manual paste-back (combined build plan, Part 1,
 * Phase 2). Wires 6b's ResearchSession shape and 6c's
 * generateNextQuery()/submitFindings()/generateFinalReport() into a
 * standalone screen, same "own top-level view" pattern as ContractDashboard
 * (5a/5b) and SanMode — not nested inside a case workspace.
 *
 * NOT YET PERSISTED — per research/types.ts's file header, 6b deliberately
 * left storage undecided. This screen therefore holds the ResearchSession in
 * plain React state only. Refreshing the page loses the session; that is the
 * documented, intentional state of the roadmap as of 6d, not a bug of this
 * screen. A future roadmap item can add persistence against the same shape
 * without this component changing its contract with 6c's functions.
 *
 * TURN FLOW — "Start Research" creates a fresh session (6c's factory) and
 * immediately calls generateNextQuery() for turn 1's query. The query is
 * then shown with a textarea: counsel goes and does the actual research
 * (LawPavilion, a textbook, whatever), pastes back what they found, and
 * hits "Submit Findings" — which calls submitFindings() to synthesize it
 * and judge exhaustion. Once an entry is complete, "Continue" calls
 * generateNextQuery() again for the next turn — never auto-loops, so
 * counsel is never left without a chance to read where the session is
 * before spending another call. "Stop Here" sets status to 'abandoned' per
 * research/types.ts's status enum, distinct from Supo's own 'exhausted'
 * verdict. "Generate Report" calls generateFinalReport() once at least one
 * turn is complete; the report is rendered read-only and can be sent to the
 * docx skill for a downloadable copy.
 */

import { useState } from 'react';
import { useAppStore } from '@/state/appStore';
import { T, S } from '@/constants/tokens';
import {
  createResearchSession,
  generateNextQuery,
  submitFindings,
  generateFinalReport,
  MAX_RESEARCH_TURNS,
} from '@/research/queryGenerator';
import type { ResearchSession } from '@/research/types';

function StatusPill({ status }: { status: ResearchSession['status'] }) {
  const copy: Record<ResearchSession['status'], { label: string; color: string }> = {
    active:    { label: 'Active',    color: T.info },
    exhausted: { label: 'Exhausted', color: T.ok },
    abandoned: { label: 'Abandoned', color: T.mute },
  };
  const { label, color } = copy[status];
  return (
    <span style={{
      fontSize: 10, color, border: `1px solid ${color}`, borderRadius: 3,
      padding: '2px 8px', fontFamily: "'Times New Roman', Times, serif",
      letterSpacing: '.08em', textTransform: 'uppercase', fontWeight: 700,
    }}>
      {label}
    </span>
  );
}

/** A completed turn — read-only, same card as before the redesign. */
function CompletedEntryCard({ index, entry }: { index: number; entry: ResearchSession['entries'][number] }) {
  return (
    <div style={{
      border: `1px solid ${T.bdrL}`, borderRadius: 5, padding: '14px 16px',
      marginBottom: 12, background: '#ffffff',
    }}>
      <p style={{ ...S.h3, margin: '0 0 6px' }}>Turn {index + 1}</p>
      <p style={{ ...S.p, fontStyle: 'italic', margin: '0 0 8px' }}>{entry.query}</p>
      <p style={{ ...S.p, margin: 0, whiteSpace: 'pre-wrap' }}>{entry.findings}</p>
      <p style={{
        fontSize: 11, color: entry.exhausted ? T.ok : T.mute, marginTop: 10,
        fontFamily: "'Times New Roman', Times, serif", lineHeight: 1.6,
        borderTop: `1px solid ${T.bdrL}`, paddingTop: 8,
      }}>
        {entry.exhausted ? '✓ ' : ''}{entry.exhausted_reasoning}
      </p>
    </div>
  );
}

/**
 * The current, `'awaiting_findings'` turn — shows the query and a textarea
 * for counsel to paste back whatever they found, plus a Submit button.
 */
function AwaitingEntryCard({
  index, entry, rawText, onChangeRawText, onSubmit, submitting, error,
}: {
  index: number;
  entry: ResearchSession['entries'][number];
  rawText: string;
  onChangeRawText: (v: string) => void;
  onSubmit: () => void;
  submitting: boolean;
  error: string;
}) {
  const canSubmit = rawText.trim().length > 0 && !submitting;
  return (
    <div style={{
      border: `1px solid ${T.text}`, borderRadius: 5, padding: '14px 16px',
      marginBottom: 12, background: T.card,
    }}>
      <p style={{ ...S.h3, margin: '0 0 6px' }}>Turn {index + 1} — awaiting your findings</p>
      <p style={{ ...S.p, fontStyle: 'italic', margin: '0 0 10px' }}>{entry.query}</p>
      <label style={S.label}>Paste what you found *</label>
      <textarea
        value={rawText}
        onChange={e => onChangeRawText(e.target.value)}
        placeholder="Paste the case text, statute section, textbook passage, or search-result text you found for this query…"
        style={S.ta}
        disabled={submitting}
      />
      {error && (
        <p style={{ fontSize: 12, color: T.err, margin: '8px 0 0' }}>{error}</p>
      )}
      <button
        type="button"
        onClick={onSubmit}
        disabled={!canSubmit}
        style={canSubmit ? S.btn : S.btnOff}
      >
        {submitting ? 'Synthesizing…' : 'Submit Findings'}
      </button>
    </div>
  );
}

export function ResearchDashboard() {
  const { setView } = useAppStore();

  const [problemStatement, setProblemStatement] = useState('');
  const [session, setSession] = useState<ResearchSession | null>(null);
  const [rawText, setRawText] = useState('');
  const [querying, setQuerying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [error, setError] = useState('');

  const canStart = problemStatement.trim().length > 0 && !querying;
  const last = session?.entries[session.entries.length - 1];
  const awaitingCurrent = last?.status === 'awaiting_findings';
  const completeCount = session?.entries.filter(e => e.status === 'complete').length ?? 0;

  async function startResearch() {
    if (!canStart) return;
    const fresh = createResearchSession(problemStatement);
    setSession(fresh);
    setQuerying(true);
    setError('');
    const result = await generateNextQuery(fresh);
    setQuerying(false);
    setSession(result.session);
    if (!result.ok) setError(result.error || 'Supo could not generate the first query.');
  }

  async function continueResearch() {
    if (!session || session.status !== 'active' || querying || awaitingCurrent) return;
    setQuerying(true);
    setError('');
    const result = await generateNextQuery(session);
    setQuerying(false);
    setSession(result.session);
    if (!result.ok) setError(result.error || 'Supo could not generate the next query.');
  }

  async function submit() {
    if (!session || !rawText.trim() || submitting) return;
    setSubmitting(true);
    setError('');
    const result = await submitFindings(session, rawText);
    setSubmitting(false);
    setSession(result.session);
    if (result.ok) setRawText('');
    if (!result.ok) setError(result.error || 'Supo could not synthesize these findings.');
  }

  async function makeReport() {
    if (!session || completeCount === 0 || reporting) return;
    setReporting(true);
    setError('');
    const result = await generateFinalReport(session);
    setReporting(false);
    setSession(result.session);
    if (!result.ok) setError(result.error || 'Could not generate the report.');
  }

  function stopHere() {
    if (!session || session.status !== 'active') return;
    setSession({ ...session, status: 'abandoned', updated_at: new Date().toISOString() });
  }

  function newSession() {
    setSession(null);
    setProblemStatement('');
    setRawText('');
    setError('');
  }

  const completedEntries = session?.entries.filter(e => e.status === 'complete') ?? [];
  const busy = querying || submitting || reporting;

  return (
    <div style={{ animation: 'fadeUp .3s ease', maxWidth: 620, margin: '0 auto' }}>

      {/* Masthead — same pattern as ContractDashboard */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <button
            onClick={() => setView('home')}
            style={{
              background: 'none', border: `1px solid ${T.bdr}`, color: T.dim,
              borderRadius: 3, padding: '6px 16px', fontSize: 12,
              fontFamily: "'Times New Roman', Times, serif", cursor: 'pointer',
            }}
          >
            ← Back
          </button>
          <p style={{
            fontSize: 9, color: T.mute, fontFamily: "'Times New Roman', Times, serif",
            letterSpacing: '.2em', textTransform: 'uppercase',
          }}>
            Smart Tools · Research
          </p>
        </div>
        <div style={{ borderTop: `2px solid ${T.text}`, paddingTop: 12 }}>
          <h1 style={{
            fontSize: 30, color: T.text, fontFamily: "'Times New Roman', Times, serif",
            fontWeight: 700, fontStyle: 'italic', lineHeight: 1.15, marginBottom: 10,
          }}>
            Research Assistant
          </h1>
          <p style={{
            fontSize: 14, color: T.sub, fontFamily: "'Times New Roman', Times, serif",
            lineHeight: 1.65, borderTop: `1px solid ${T.bdr}`, paddingTop: 10,
          }}>
            Supo asks one query at a time, up to {MAX_RESEARCH_TURNS} turns. You go find the
            answer and paste it back — Supo only ever writes up what you actually found.
          </p>
        </div>
      </div>

      {!session && (
        <div style={{
          background: T.card, border: `1px solid ${T.bdr}`, borderRadius: 6,
          padding: '20px 20px 22px',
        }}>
          <label style={S.label}>Problem Statement *</label>
          <textarea
            value={problemStatement}
            onChange={e => setProblemStatement(e.target.value)}
            placeholder="State the legal question or research problem counsel needs resolved…"
            style={S.ta}
            disabled={querying}
          />
          <button
            type="button"
            onClick={startResearch}
            disabled={!canStart}
            style={canStart ? S.btn : S.btnOff}
          >
            {querying ? 'Generating query 1…' : 'Start Research'}
          </button>
        </div>
      )}

      {session && (
        <>
          {/* Session header strip */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 14, padding: '10px 14px', background: T.card,
            border: `1px solid ${T.bdrL}`, borderRadius: 4, gap: 12,
          }}>
            <div style={{ minWidth: 0 }}>
              <p style={{
                fontSize: 13, color: T.text, fontFamily: "'Times New Roman', Times, serif",
                margin: '0 0 4px', lineHeight: 1.5,
              }}>
                {session.problem_statement}
              </p>
              <p style={{ ...S.hint, margin: 0 }}>
                {completeCount} of {MAX_RESEARCH_TURNS} turns complete
              </p>
            </div>
            <StatusPill status={session.status} />
          </div>

          {!session.final_report && completedEntries.map((entry, i) => (
            <CompletedEntryCard key={entry.id} index={i} entry={entry} />
          ))}

          {!session.final_report && awaitingCurrent && last && (
            <AwaitingEntryCard
              index={session.entries.length - 1}
              entry={last}
              rawText={rawText}
              onChangeRawText={setRawText}
              onSubmit={submit}
              submitting={submitting}
              error={error}
            />
          )}

          {error && !awaitingCurrent && (
            <div style={{
              marginBottom: 14, padding: '10px 14px', background: '#fff4f4',
              border: `1px solid ${T.err}`, borderRadius: 5,
            }}>
              <p style={{ ...S.p, color: T.err, margin: 0, fontSize: 12 }}>{error}</p>
            </div>
          )}

          {session.final_report && (
            <div style={{
              background: '#ffffff', border: `1px solid ${T.bdr}`, borderRadius: 6,
              padding: '22px 24px', marginBottom: 14,
            }}>
              <p style={{ ...S.h3, margin: '0 0 10px' }}>Final Report</p>
              <div style={{
                fontSize: 14, color: T.sub, fontFamily: "'Times New Roman', Times, serif",
                lineHeight: 1.85, whiteSpace: 'pre-wrap',
              }}>
                {session.final_report}
              </div>
            </div>
          )}

          {!session.final_report && !awaitingCurrent && session.status === 'active' && (
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={continueResearch}
                disabled={busy}
                style={{ ...(busy ? S.btnOff : S.btn), marginTop: 0, flex: 2 }}
              >
                {querying ? `Generating query ${session.entries.length + 1}…` : 'Continue'}
              </button>
              <button
                type="button"
                onClick={stopHere}
                disabled={busy}
                style={{
                  flex: 1, background: 'none', border: `1px solid ${T.bdr}`, color: T.dim,
                  borderRadius: 4, padding: '11px 14px', fontSize: 12, cursor: busy ? 'not-allowed' : 'pointer',
                  fontFamily: "'Times New Roman', Times, serif",
                }}
              >
                Stop Here
              </button>
            </div>
          )}

          {!session.final_report && !awaitingCurrent && completeCount > 0 && (
            <button
              type="button"
              onClick={makeReport}
              disabled={busy}
              style={{ ...(busy ? S.btnOff : S.btn), background: busy ? undefined : T.dim }}
            >
              {reporting ? 'Writing report…' : 'Generate Report'}
            </button>
          )}

          {(session.status !== 'active' || session.final_report) && (
            <button type="button" onClick={newSession} style={{ ...S.btn, background: T.dim }}>
              New Research Session
            </button>
          )}
        </>
      )}

      <p style={{
        marginTop: 32, fontSize: 11, color: T.mute, textAlign: 'center',
        fontFamily: "'Times New Roman', Times, serif", lineHeight: 1.8,
      }}>
        AFS Legal OS · Research Assistant — one query at a time, your own research, an honest exhaustion verdict.
      </p>
    </div>
  );
}
